/**
 * totp-settings-section.tsx — Cài đặt cá nhân → Bảo mật → Xác thực 2 lớp (TOTP).
 *
 * Hiện trạng thái (đã bật / chưa — trường `mandatory` luôn false hiện tại, không vai trò nào bị bắt buộc kỹ
 * thuật nữa, xem totp-service.ts) + 3 hành động: Bật (quét QR → xác nhận mã → hiện 10 mã dự phòng MỘT LẦN),
 * Tắt (đòi xác thực lại — mật khẩu HOẶC mã hiện tại), Tạo lại mã dự phòng (cũng đòi xác thực lại — bộ mã cũ
 * bị vô hiệu hoá ngay).
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Check, Copy, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { apiError } from '@/lib/api-client'
import { Loading, ErrorState } from '@/components/shared/feedback'
import {
  useTotpStatus, useTotpSetupStart, useTotpSetupConfirm, useTotpDisable, useTotpRegenerateBackupCodes,
} from '@/hooks/use-settings'
import { AccountRow } from './account-ui'

function BackupCodesGrid({ codes }: { codes: string[] }) {
  const { t } = useTranslation('accountPages')
  const [copied, setCopied] = useState(false)
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        <Trans t={t} i18nKey="totp.backupCodesHint" components={{ strong: <strong /> }} />
      </p>
      <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted p-4 font-mono text-sm">
        {codes.map((c) => <div key={c}>{c}</div>)}
      </div>
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => {
          void navigator.clipboard?.writeText(codes.join('\n')).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
          })
        }}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        {copied ? t('totp.copied') : t('totp.copyAll')}
      </Button>
    </div>
  )
}

/** Bật TOTP: quét QR → nhập mã đầu → hiện mã dự phòng. */
function EnableTotpDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation(['accountPages', 'common'])
  const start = useTotpSetupStart()
  const confirm = useTotpSetupConfirm()
  const [qr, setQr] = useState<{ otpauthUri: string; qrDataUrl: string } | null>(null)
  const [code, setCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)

  function reset() {
    setQr(null)
    setCode('')
    setBackupCodes(null)
  }

  // `open` được điều khiển từ ngoài (nút "Bật xác thực 2 lớp" ở TotpSettingsSection gọi setEnableOpen(true)
  // thẳng, không qua DialogTrigger) — Radix chỉ gọi onOpenChange cho sự kiện NÓ tự khởi xướng (Esc, overlay,
  // nút Đóng), không gọi lại khi prop open đổi từ ngoài. Phải tự theo dõi open để sinh QR lúc mở, nếu không
  // hộp thoại mở ra nhưng không bao giờ gọi setup/start — kẹt vòng xoay mãi mãi.
  useEffect(() => {
    if (open && !qr && !start.isPending) {
      start.mutate(undefined, { onSuccess: setQr, onError: (err) => toast.error(apiError(err)) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ muốn chạy khi open chuyển true, không lặp lại theo qr/start
  }, [open])

  function onShow(o: boolean) {
    if (!o) reset()
    onOpenChange(o)
  }

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault()
    try {
      const { backupCodes: codes } = await confirm.mutateAsync(code.trim())
      setBackupCodes(codes)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const secret = qr?.otpauthUri.match(/secret=([^&]+)/)?.[1]

  return (
    <Dialog open={open} onOpenChange={onShow}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('accountPages:totp.enableTitle')}</DialogTitle>
          {!backupCodes && (
            <DialogDescription>{t('accountPages:totp.enableDescription')}</DialogDescription>
          )}
        </DialogHeader>

        {backupCodes ? (
          <>
            <div className="flex items-center gap-2 text-emerald-600">
              <ShieldCheck className="h-5 w-5" />
              <p className="text-sm font-medium">{t('accountPages:totp.enabled')}</p>
            </div>
            <BackupCodesGrid codes={backupCodes} />
            <DialogFooter>
              <Button type="button" onClick={() => onShow(false)}>{t('accountPages:totp.savedClose')}</Button>
            </DialogFooter>
          </>
        ) : start.isPending || !qr ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <form onSubmit={onConfirm} className="space-y-4">
            <div className="flex flex-col items-center gap-2">
              <img src={qr.qrDataUrl} alt={t('accountPages:totp.qrAlt')} className="h-44 w-44 rounded-lg border p-2" />
              {secret && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer">{t('accountPages:totp.manualEntry')}</summary>
                  <code className="mt-1 block break-all">{secret}</code>
                </details>
              )}
            </div>
            <div>
              <Label htmlFor="enable-totp-code">{t('accountPages:totp.codeLabel')}</Label>
              <Input
                id="enable-totp-code" className="mt-1" inputMode="numeric" autoComplete="one-time-code"
                placeholder={t('accountPages:totp.codePlaceholder')} value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} autoFocus required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onShow(false)}>{t('common:actions.cancel')}</Button>
              <Button type="submit" disabled={confirm.isPending || code.trim().length !== 6}>
                {confirm.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('accountPages:totp.confirmEnable')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Xác thực lại (mật khẩu HOẶC mã TOTP hiện tại) trước khi Tắt hoặc Tạo lại mã dự phòng. */
function ReauthDialog({
  open, onOpenChange, title, description, confirmLabel, onConfirm, resultView,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  title: string
  description: string
  confirmLabel: string
  onConfirm: (opts: { password?: string; code?: string }) => Promise<void>
  /** Hiện sau khi thành công thay cho form (ví dụ: bộ mã dự phòng mới) — null = đóng ngay. */
  resultView?: React.ReactNode
}) {
  const { t } = useTranslation(['accountPages', 'common'])
  const [mode, setMode] = useState<'password' | 'code'>('password')
  const [value, setValue] = useState('')
  const [pending, setPending] = useState(false)
  const [done, setDone] = useState(false)

  function onShow(o: boolean) {
    if (!o) {
      setValue('')
      setMode('password')
      setDone(false)
    }
    onOpenChange(o)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setPending(true)
    try {
      await onConfirm(mode === 'password' ? { password: value } : { code: value })
      if (resultView) setDone(true)
      else onShow(false)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onShow}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {!done && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {done ? (
          <>
            {resultView}
            <DialogFooter><Button type="button" onClick={() => onShow(false)}>{t('common:actions.close')}</Button></DialogFooter>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="flex gap-2 text-sm">
              <button type="button" onClick={() => { setMode('password'); setValue('') }} className={mode === 'password' ? 'font-medium text-primary underline' : 'text-muted-foreground'}>
                {t('accountPages:totp.usePassword')}
              </button>
              <span className="text-muted-foreground">·</span>
              <button type="button" onClick={() => { setMode('code'); setValue('') }} className={mode === 'code' ? 'font-medium text-primary underline' : 'text-muted-foreground'}>
                {t('accountPages:totp.useCurrentCode')}
              </button>
            </div>
            <div>
              <Label htmlFor="reauth-value">{mode === 'password' ? t('accountPages:totp.currentPasswordLabel') : t('accountPages:totp.currentCodeLabel')}</Label>
              <Input
                id="reauth-value" className="mt-1" type={mode === 'password' ? 'password' : 'text'}
                autoComplete={mode === 'password' ? 'current-password' : 'one-time-code'}
                value={value} onChange={(e) => setValue(e.target.value)} autoFocus required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onShow(false)}>{t('common:actions.cancel')}</Button>
              <Button type="submit" variant="destructive" disabled={pending || !value}>
                {pending && <Loader2 className="h-4 w-4 animate-spin" />}
                {confirmLabel}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function TotpSettingsSection() {
  const { t } = useTranslation('accountPages')
  const { data, isLoading, isError } = useTotpStatus()
  const disableTotp = useTotpDisable()
  const regenerate = useTotpRegenerateBackupCodes()
  const [enableOpen, setEnableOpen] = useState(false)
  const [disableOpen, setDisableOpen] = useState(false)
  const [regenOpen, setRegenOpen] = useState(false)
  const [newCodes, setNewCodes] = useState<string[]>([])

  if (isLoading) return <Loading label={t('totp.statusLoading')} />
  if (isError || !data) return <ErrorState />

  return (
    <>
      <AccountRow
        label={t('totp.label')}
        hint={data.mandatory ? t('totp.mandatoryHint') : t('totp.optionalHint')}
      >
        <div className="flex flex-wrap items-center gap-2">
          {data.enabled ? (
            <Badge variant="success" className="gap-1"><ShieldCheck className="h-3.5 w-3.5" /> {t('totp.enabledBadge')}</Badge>
          ) : (
            <Badge variant="secondary" className="gap-1"><ShieldAlert className="h-3.5 w-3.5" /> {t('totp.disabledBadge')}</Badge>
          )}
          {data.mandatory && <Badge variant="warning">{t('totp.mandatoryBadge')}</Badge>}

          {!data.enabled && (
            <Button type="button" variant="outline" size="sm" onClick={() => setEnableOpen(true)}>{t('totp.enableButton')}</Button>
          )}
          {data.enabled && (
            <Button type="button" variant="outline" size="sm" onClick={() => setRegenOpen(true)}>{t('totp.regenerateButton')}</Button>
          )}
          {data.enabled && !data.mandatory && (
            <Button type="button" variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDisableOpen(true)}>
              {t('totp.disableButton')}
            </Button>
          )}
        </div>
      </AccountRow>

      <EnableTotpDialog open={enableOpen} onOpenChange={setEnableOpen} />

      <ReauthDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        title={t('totp.disableTitle')}
        description={t('totp.disableDescription')}
        confirmLabel={t('totp.disableConfirm')}
        onConfirm={async (opts) => {
          await disableTotp.mutateAsync(opts)
          toast.success(t('totp.disabled'))
        }}
      />

      <ReauthDialog
        open={regenOpen}
        onOpenChange={setRegenOpen}
        title={t('totp.regenerateButton')}
        description={t('totp.regenerateDescription')}
        confirmLabel={t('totp.regenerateConfirm')}
        onConfirm={async (opts) => {
          const { backupCodes } = await regenerate.mutateAsync(opts)
          setNewCodes(backupCodes)
        }}
        resultView={<BackupCodesGrid codes={newCodes} />}
      />
    </>
  )
}
